import type { CSSProperties } from 'react';

import { ScreenReaderOnly } from '@capsule/consumer-design-system';

import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import InlineSVG from 'svg-inline-react';

import SystemNavCloseIcon from 'public/nav-close.svg';
import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { TransparentButton } from 'src/app/components/Elements/Button/TransparentButton';
import { FocusLockAndEscape } from 'src/app/components/PageSections/CardItemOverflowMenuWrapper/FocusLockAndEscape';
import { useIsUserTabbing } from 'src/app/hooks/useIsUserTabbing';
import { useLazySegmentTrack } from 'src/app/hooks/useLazySegmentTrack';
import { CLOSE_MODAL_BUTTON } from 'src/consts/testIDs';

import styles from './styles.module.scss';

interface BaseModalProps {
  handleModalClosed: () => void;
  showModal: boolean;
  closedEvent?: UnifiedEvent;
  closedEventPayload?: { [key: string]: string };
  style?: CSSProperties;
  ariaLabeledById?: string;
  ariaDescribedById?: string;
  showCloseIcon?: boolean;
  size?: 'small' | 'medium';
}

export const BaseModal = ({
  className,
  handleModalClosed,
  showModal,
  closedEvent,
  closedEventPayload,
  children,
  style,
  ariaLabeledById,
  ariaDescribedById,
  showCloseIcon = true,
  size = 'medium',
}: BaseModalProps) => {
  const [trackSegmentEvent] = useLazySegmentTrack(closedEvent);
  const shouldShowOutline = useIsUserTabbing();

  const handleClick = () => {
    handleModalClosed();
    if (closedEventPayload) trackSegmentEvent(closedEventPayload);
  };

  return (
    <CSSTransition
      in={showModal}
      mountOnEnter
      classNames={{
        enter: styles.enter,
        enterActive: styles.enterActive,
        exit: styles.exit,
        exitActive: styles.exitActive,
      }}
      timeout={1000}
      unmountOnExit
    >
      <FocusLockAndEscape handleEscapePressed={handleModalClosed}>
        <div className={styles.component}>
          <div className={styles.overlay} onClick={handleClick} />

          <div
            style={style}
            className={classNames(styles.messageBox, className, styles[size])}
            role="dialog"
            aria-modal="true"
            aria-labelledby={ariaLabeledById}
            aria-describedby={ariaDescribedById}
          >
            {showCloseIcon && (
              <TransparentButton
                className={classNames(styles.close, !shouldShowOutline && styles.hideOutline)}
                onClick={handleClick}
                data-testid={CLOSE_MODAL_BUTTON}
              >
                <ScreenReaderOnly>Close Dialog</ScreenReaderOnly>
                <InlineSVG src={SystemNavCloseIcon} raw />
              </TransparentButton>
            )}
            {children}
          </div>
        </div>
      </FocusLockAndEscape>
    </CSSTransition>
  );
};
